// src/components/Privacy.js
import { useState, useEffect } from "react";
import Header from "./Header";
import Footer from "./Footer";

const CONTENT = {
  ar: {
    docTitle: "إشعار الخصوصية لمقدّمي الخدمة",
    subtitle: "تطبيق ترستفيكس — TrustFix",
    updated: "آخر تحديث: [08/29/2026]",
    intro:
      "يوضّح إشعار الخصوصية هذا كيف تجمع ترستفيكس معلوماتك الشخصية وتستخدمها وتحميها عند استخدامك للمنصة كشريك.",
    back: "العودة إلى الصفحة الرئيسية",
    sections: [
      ["١. المعلومات التي نجمعها", "نجمع المعلومات التي تقدّمها عند التسجيل، بما في ذلك اسمك ورقم هاتفك وبريدك الإلكتروني وفئات الخدمات التي تعرضها. كما نجمع بيانات الموقع ومعلومات الجهاز ونشاط الاستخدام لتشغيل المنصة."],
      ["٢. كيف نستخدم معلوماتك", "نستخدم معلوماتك لإنشاء حسابك وإدارته، ولمطابقتك مع المهام القريبة، ولمعالجة رسوم المنصة، وإرسال الإشعارات، وتقديم الدعم، وتحسين خدماتنا."],
      ["٣. بيانات الموقع", "لعرض المهام عليك وتمكين التنقّل، نجمع موقع جهازك أثناء نشاطك على المنصة. ويمكنك التحكم في الوصول إلى الموقع عبر إعدادات جهازك، مع أن بعض الميزات قد لا تعمل بدونه."],
      ["٤. مشاركة معلوماتك", "نشارك معلومات محدودة مع العملاء لتمكين الخدمات (مثل اسمك وموقعك العام). وقد نشارك البيانات مع مزوّدي الخدمات الذين يساعدوننا في تشغيل المنصة، ومع الجهات المختصة عند اشتراط النظام لذلك."],
      ["٥. أمان البيانات", "نتخذ تدابير معقولة لحماية معلوماتك. ومع ذلك، لا توجد وسيلة نقل أو تخزين آمنة تمامًا، ولا يمكننا ضمان الأمان المطلق."],
      ["٦. الاحتفاظ بالبيانات", "نحتفظ بمعلوماتك طوال فترة نشاط حسابك وبالقدر اللازم للامتثال للالتزامات النظامية. وإذا عطّلت حسابك، فقد نحتفظ ببعض السجلات عند اشتراط ذلك."],
      ["٧. حقوقك", "يمكنك الوصول إلى معلوماتك الشخصية أو تحديثها أو طلب حذفها، وفقًا للأنظمة المعمول بها. ويمكنك إدارة الكثير من معلوماتك مباشرة داخل التطبيق."],
      ["٨. تعديلات على هذا الإشعار", "قد نُحدّث إشعار الخصوصية هذا من وقت لآخر. وسنُعلمك بالتغييرات الجوهرية عبر التطبيق."],
      ["٩. التواصل", "للاستفسار عن خصوصيتك، تواصل معنا عبر قسم الدعم في التطبيق."],
    ],
  },
  en: {
    docTitle: "Service Provider Privacy Notice",
    subtitle: "TrustFix",
    updated: "Last updated: [08/29/2026]",
    intro:
      "This Privacy Notice explains how TrustFix collects, uses, and protects your personal information when you use our platform as a partner.",
    back: "Back to home",
    sections: [
      ["1. Information We Collect", "We collect information you provide during registration, including your name, phone number, email address, and the service categories you offer. We also collect location data, device information, and usage activity to operate the platform."],
      ["2. How We Use Your Information", "We use your information to create and manage your account, match you with nearby jobs, process platform fees, send notifications, provide support, and improve our services."],
      ["3. Location Data", "To offer you jobs and enable navigation, we collect your device location while you are active on the platform. You can control location access through your device settings, though some features may not work without it."],
      ["4. Sharing Your Information", "We share limited information with customers to enable services (such as your name and general location). We may share data with service providers who help us operate the platform, and with authorities where required by law."],
      ["5. Data Security", "We take reasonable measures to protect your information. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security."],
      ["6. Data Retention", "We retain your information for as long as your account is active and as needed to comply with legal obligations. If you disable your account, we retain certain records where required."],
      ["7. Your Rights", "You may access, update, or request deletion of your personal information, subject to applicable law. You can manage much of your information directly in the app."],
      ["8. Changes to This Notice", "We may update this Privacy Notice from time to time. We will notify you of significant changes through the app."],
      ["9. Contact", "For questions about your privacy, contact us through the app's support section."],
    ],
  },
};

export default function Privacy() {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("tf_lang") || "ar";
    } catch {
      return "ar";
    }
  });

  const isAr = lang === "ar";
  const t = CONTENT[lang];

  useEffect(() => {
    try {
      localStorage.setItem("tf_lang", lang);
    } catch {}
  }, [lang]);

  return (
    <div className="min-h-screen bg-gray-50">
        <Header lang={lang} setLang={setLang} />
        <div className="min-h-screen bg-gray-150" dir={isAr ? "rtl" : "ltr"}>
          <section >
            <div className="max-w-4xl mx-auto px-5 pt-3 pb-3">
                <h1 className="text-[26px] font-extrabold text-[#006C35] mt-2 mb-1">
                {t.docTitle}
                </h1>
                <p className="text-gray-500 text-[15px] m-0">{t.subtitle}</p>
                <p className="text-gray-400 text-[13px] mt-1 mb-6">{t.updated}</p>
            </div>
          </section> 
          <section className="bg-white">
            <div className="max-w-4xl mx-auto px-5 pt-8 pb-8">
                <p className="text-gray-700 mb-3">{t.intro}</p>

                {t.sections.map(([heading, body]) => (
                <div key={heading}>
                <h2 className="text-lg font-bold text-[#006C35] mt-6 mb-2">
                        {heading}
                    </h2>
                    <p className="text-gray-700 mb-3 leading-relaxed">{body}</p>
                </div>
                ))}
            </div>
          </section>
        </div>    
        <Footer lang={lang} />

    </div>
  );
}
